import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { AlertTriangle, RefreshCw, UserX, Clock, ChevronDown, ChevronUp, CheckSquare, Square } from 'lucide-react';

const UnusedLeads = () => {
  const [summary, setSummary] = useState(null);
  const [expandedCaller, setExpandedCaller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reclaiming, setReclaiming] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [callerLeads, setCallerLeads] = useState({});
  const [loadingCallerLeads, setLoadingCallerLeads] = useState({});

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const response = await api.getUnusedLeadsSummary();
      setSummary(response.data);
    } catch (error) {
      toast.error('Failed to fetch unused leads summary');
    } finally {
      setLoading(false);
    }
  };

  const fetchCallerLeads = async (callerId, leadIds) => {
    setLoadingCallerLeads(prev => ({ ...prev, [callerId]: true }));
    try {
      const response = await api.getUnusedLeads(24);
      const leads = response.data.unused_leads.filter(lead => leadIds.includes(lead.id));
      setCallerLeads(prev => ({ ...prev, [callerId]: leads }));
    } catch (error) {
      toast.error('Failed to fetch leads for caller');
    } finally {
      setLoadingCallerLeads(prev => ({ ...prev, [callerId]: false }));
    }
  };

  const toggleCallerExpand = async (callerId, leadIds) => {
    if (expandedCaller === callerId) {
      setExpandedCaller(null);
    } else {
      setExpandedCaller(callerId);
      if (!callerLeads[callerId]) {
        await fetchCallerLeads(callerId, leadIds);
      }
    }
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAllForCaller = (callerId, leadIds) => {
    const allSelected = leadIds.every(id => selectedLeads.includes(id));
    if (allSelected) {
      setSelectedLeads(prev => prev.filter(id => !leadIds.includes(id)));
    } else {
      setSelectedLeads(prev => [...new Set([...prev, ...leadIds])]);
    }
  };

  const handleReclaimSelected = async () => {
    if (selectedLeads.length === 0) {
      toast.error('No leads selected');
      return;
    }

    if (!window.confirm(`Are you sure you want to reclaim ${selectedLeads.length} lead(s)? They will be unassigned and available for reassignment.`)) {
      return;
    }

    setReclaiming(true);
    try {
      const response = await api.reclaimLeads(selectedLeads);
      toast.success(response.data.message);
      setSelectedLeads([]);
      setCallerLeads({});
      setExpandedCaller(null);
      await fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reclaim leads');
    } finally {
      setReclaiming(false);
    }
  };

  const handleReclaimAllForCaller = async (callerId, leadIds) => {
    if (!window.confirm(`Are you sure you want to reclaim all ${leadIds.length} lead(s) from this caller?`)) {
      return;
    }

    setReclaiming(true);
    try {
      const response = await api.reclaimLeads(leadIds);
      toast.success(response.data.message);
      setSelectedLeads(prev => prev.filter(id => !leadIds.includes(id)));
      setCallerLeads(prev => {
        const newState = { ...prev };
        delete newState[callerId];
        return newState;
      });
      setExpandedCaller(null);
      await fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reclaim leads');
    } finally {
      setReclaiming(false);
    }
  };

  const formatTimeAgo = (isoString) => {
    if (!isoString) return 'Unknown';
    const date = new Date(isoString);
    const now = new Date();
    const diffHours = Math.round((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else {
      const diffDays = Math.round(diffHours / 24);
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600 font-body">Loading unused leads...</div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-headings uppercase tracking-tight text-slate-900">
            Unused Leads
          </h1>
          <p className="text-slate-600 font-body mt-1">
            Leads assigned but not contacted within 24 hours
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="refresh-unused-leads-btn"
            onClick={fetchSummary}
            className="flex items-center gap-2 px-4 py-2 bg-white text-slate-700 border border-slate-200 rounded-md hover:bg-slate-50 transition-all shadow-sm font-body"
          >
            <RefreshCw size={18} />
            <span>Refresh</span>
          </button>

          {selectedLeads.length > 0 && (
            <button
              data-testid="reclaim-selected-btn"
              onClick={handleReclaimSelected}
              disabled={reclaiming}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-all shadow-sm font-body disabled:opacity-50"
            >
              <UserX size={18} />
              <span>{reclaiming ? 'Reclaiming...' : `Reclaim Selected (${selectedLeads.length})`}</span>
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-100 rounded-lg">
              <AlertTriangle className="text-amber-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500 font-body">Total Unused Leads</p>
              <p className="text-2xl font-bold text-slate-900">{summary?.total_unused_leads || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <UserX className="text-blue-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500 font-body">Callers with Unused</p>
              <p className="text-2xl font-bold text-slate-900">{summary?.total_callers_with_unused || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-slate-100 rounded-lg">
              <Clock className="text-slate-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500 font-body">Threshold</p>
              <p className="text-2xl font-bold text-slate-900">{summary?.threshold_hours || 24} hours</p>
            </div>
          </div>
        </div>
      </div>

      {/* Callers with Unused Leads */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-semibold text-slate-900 font-headings">Unused Leads by Caller</h2>
        </div>

        {!summary?.summary || summary.summary.length === 0 ? (
          <div className="px-6 py-12 text-center text-slate-500 font-body">
            <AlertTriangle className="mx-auto mb-3 text-green-500" size={48} />
            <p className="text-lg font-medium text-green-700">No unused leads found!</p>
            <p className="text-sm text-slate-500 mt-1">All assigned leads have been contacted within 24 hours.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {summary.summary.map((item) => (
              <div key={item._id} className="bg-white">
                {/* Caller Row */}
                <div 
                  className="px-6 py-4 flex items-center justify-between hover:bg-slate-50 cursor-pointer"
                  onClick={() => toggleCallerExpand(item._id, item.lead_ids)}
                  data-testid={`caller-row-${item._id}`}
                >
                  <div className="flex items-center gap-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectAllForCaller(item._id, item.lead_ids);
                      }}
                      className="p-1 hover:bg-slate-200 rounded"
                      data-testid={`select-all-caller-${item._id}`}
                    >
                      {item.lead_ids.every(id => selectedLeads.includes(id)) ? (
                        <CheckSquare className="text-amber-600" size={20} />
                      ) : (
                        <Square className="text-slate-400" size={20} />
                      )}
                    </button>
                    <div>
                      <p className="font-medium text-slate-900 font-body">{item.caller_name || 'Unknown Caller'}</p>
                      <p className="text-sm text-slate-500">
                        Oldest: {formatTimeAgo(item.oldest_assignment)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
                      {item.count} lead{item.count !== 1 ? 's' : ''}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReclaimAllForCaller(item._id, item.lead_ids);
                      }}
                      disabled={reclaiming}
                      className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 font-body"
                      data-testid={`reclaim-all-${item._id}`}
                    >
                      Reclaim All
                    </button>
                    {expandedCaller === item._id ? (
                      <ChevronUp className="text-slate-400" size={20} />
                    ) : (
                      <ChevronDown className="text-slate-400" size={20} />
                    )}
                  </div>
                </div>

                {/* Expanded Leads */}
                {expandedCaller === item._id && (
                  <div className="bg-slate-50 px-6 py-4 border-t border-slate-200">
                    {loadingCallerLeads[item._id] ? (
                      <p className="text-slate-500 text-center py-4">Loading leads...</p>
                    ) : callerLeads[item._id] ? (
                      <div className="space-y-2">
                        {callerLeads[item._id].map((lead) => (
                          <div 
                            key={lead.id}
                            className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-slate-200"
                            data-testid={`lead-item-${lead.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => handleSelectLead(lead.id)}
                                className="p-1 hover:bg-slate-100 rounded"
                              >
                                {selectedLeads.includes(lead.id) ? (
                                  <CheckSquare className="text-amber-600" size={18} />
                                ) : (
                                  <Square className="text-slate-400" size={18} />
                                )}
                              </button>
                              <div>
                                <p className="font-medium text-slate-900 text-sm">{lead.name}</p>
                                <p className="text-xs text-slate-500">{lead.phone} • {lead.course || 'No course'}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-amber-600 font-medium">
                                {lead.hours_since_assignment}h since assignment
                              </p>
                              <p className="text-xs text-slate-500">
                                Assigned: {new Date(lead.assigned_at).toLocaleString('en-IN')}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-4">No leads data</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h3 className="font-semibold text-blue-900 mb-2">About Unused Leads</h3>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>Leads are marked as "unused" if they remain in "new" status 24 hours after assignment</li>
          <li>Reclaiming leads will unassign them, making them available for reassignment</li>
          <li>Affected callers will be notified when their leads are reclaimed</li>
          <li>Leads can now be assigned to callers who have logged in within the last 4 days (not just online)</li>
        </ul>
      </div>
    </div>
  );
};

export default UnusedLeads;
